import { useCallback, useMemo } from "react";
import { QueryClient, QueryKey, useQueryClient } from "@tanstack/react-query";
import {
  type AxiosRequestConfig,
  type AxiosResponse,
  type Method
} from "axios";
import type { StringifiableRecord } from "query-string";
import type { router } from "react-query-kit";
import { PageableOf } from "@components/table/_components/pageable/_types/pageable.types";
import { axiosInstance } from "@services/api/api.service";
import { SearchPayload } from "@src/app/filters/search.hook";
import { BaseEntity } from "@src/types/request.types";

type EndpointPath = string;
type Form = { id?: string | number } & Record<string, unknown>;
export type Payload = Form | FormData | SearchPayload | null;
export type QueryParams = StringifiableRecord;

const apiRequest = async <T>({
  method,
  path,
  payload = null,
  queryParams = null
}: {
  method: Method;
  path: EndpointPath;
  payload?: Payload;
  queryParams?: QueryParams | null;
}): Promise<T> => {
  const config: AxiosRequestConfig = {
    method,
    baseURL: path,
    data: payload,
    params: queryParams
  };

  const response: AxiosResponse<T> = await axiosInstance.request<T>(config);
  return response.data;
};

const getRequest = async <T>(
  path: EndpointPath,
  queryParams?: QueryParams
): Promise<T> => {
  return apiRequest<T>({
    method: "get",
    path,
    queryParams
  });
};

const getPagedRequest = async <T>(
  path: EndpointPath,
  queryParams?: QueryParams
): Promise<PageableOf<T>> => {
  return getRequest<PageableOf<T>>(path, queryParams);
};

const postRequest = async <T>(
  path: EndpointPath,
  payload: Payload,
  queryParams?: QueryParams
): Promise<T> => {
  return apiRequest<T>({
    method: "post",
    path,
    payload,
    queryParams
  });
};

const postRequestWithPagedResponse = async <T>(
  path: EndpointPath,
  payload: Payload,
  queryParams?: QueryParams
): Promise<PageableOf<T>> => {
  return apiRequest<PageableOf<T>>({
    method: "post",
    path,
    payload,
    queryParams
  });
};

const putRequest = async <T>(path: EndpointPath, form: Form): Promise<T> => {
  return apiRequest<T>({
    method: "put",
    path,
    payload: form
  });
};

const saveRequest = async <T>(path: EndpointPath, form: Form): Promise<T> => {
  const id = form.id;
  const method: Method = id ? "put" : "post";
  const url: string = id ? `${path}/${id}` : path;

  return apiRequest<T>({
    method,
    path: url,
    payload: form
  });
};

const deleteRequest = async <T extends BaseEntity>(
  path: EndpointPath,
  id?: number | string
): Promise<T> => {
  if (!id) {
    return Promise.reject(new Error("Id is required for delete request"));
  }
  const url = `${path}/${id}`;

  return apiRequest<T>({
    method: "delete",
    path: url
  });
};

const deleteRequestPath = async <T>(path: EndpointPath): Promise<T> => {
  return apiRequest<T>({
    method: "delete",
    path
  });
};

const buildBaseUrl = (baseUrl: string): string => {
  return `/api${baseUrl}`;
};

// Clears all cache for the given router, use more specific cache invalidation if possible.
const invalidateQueryCaches = async (
  queryClient: QueryClient,
  queryRouter: ReturnType<typeof router>
) => {
  const queryKeys: string[] = Object.keys(queryRouter as object).filter(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    (key: string) => (queryRouter as Record<string, any>)[key]?.getKey
  );

  for (const key of queryKeys) {
    // eslint-disable-next-line
    const queryKey: QueryKey = queryRouter[key].getKey();
    if (queryKey) {
      await queryClient.invalidateQueries({ queryKey });
    }
  }
};

const useBaseUrl = (baseUrl: string) => {
  return useMemo(() => buildBaseUrl(baseUrl), [baseUrl]);
};

const useInvalidate = (api: ReturnType<typeof router>) => {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await invalidateQueryCaches(queryClient, api);
  }, [queryClient, api]);
};

export {
  buildBaseUrl,
  getRequest,
  saveRequest,
  postRequest,
  putRequest,
  postRequestWithPagedResponse,
  deleteRequest,
  deleteRequestPath,
  getPagedRequest,
  invalidateQueryCaches,
  useBaseUrl,
  useInvalidate
};
